/**
 * What this app does with the label your code action puts on a finished call.
 *
 * Arun, looking at the first version of this tab: "didnt understand the outcomes tab -
 * why do we need that if the agent sends whihc numbers to call all the time". A fair
 * question, and the honest answer is narrow: the app needs this page for exactly two
 * decisions, and the old layout hid both of them behind eight identical-looking rows
 * with four columns each.
 *
 * The two decisions:
 *
 *  1. **Is this label worth a callback at all?** The code action decides what to post;
 *     this decides what we do when it arrives. Arun's rule for what gets posted — "we
 *     will add call back date and time and only those calls will come here as well as
 *     one amrked as end reason voicemail - this is a config FDE decides whether to send
 *     vociemail or not" — is a decision made in the code action, so these switches are
 *     the backstop underneath it: something that arrives with a label we do not accept
 *     is recorded and produces no call.
 *  2. **How long after the call do we dial, when nobody named a time?** If the person
 *     said "call me at four", that wins and none of this applies. A voicemail or a
 *     no-answer never names a time, which is the case Arun described: "only cases would
 *     be no answer where a retry goes after x minutes/hours".
 *
 * Everything else about what was *said* on the call belongs to the code action, because
 * this app never sees a transcript. That sentence is now on the page.
 *
 * **Five rows, in two groups, and two closing facts (2026-09-04).** The page used to list all
 * eight outcomes. Four of them left, and `NOT_SETTINGS` below carries the argument: two are
 * dead because the generated hook does not post them, and two were never policy because they
 * block a number rather than schedule a call. What is left is the two groups that genuinely are
 * a decision — someone asked to be called back (with an hour, or without one), and nobody
 * spoke to the agent.
 *
 * A tenant's own disposition vocabulary used to be printed at the bottom of this tab, as a
 * survey across every agent on the account. Arun: *"move your agents own dispositions under
 * the agent."* It now lives on the agent whose values they are — block 3 of
 * `code-action-section.tsx`, which is per agent and explains why that is the better place for
 * it. This tab is account policy only.
 *
 * ## The callback case is two rows (2026-09-03)
 *
 * Arun: *"fold the require callback time thing into outcomes - callback with a time and
 * callback without a time - and let me adjust it there. drop the switch."* So the first group
 * has two rows: a request that named an hour, and a request that did not. The second is
 * `callback_no_time`, which is a rule and not an outcome — nothing may post it, it is the
 * branch this app takes when the time it was sent resolves to nothing. Switching it off
 * records the call and never dials it; the old per-agent switch dropped the call inside the
 * tenant's script, so the request left no trace anywhere and changing your mind meant
 * reinstalling the hook.
 */

import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, Info } from "lucide-react";
import { toast } from "sonner";
import { SettingsSection } from "@/components/ui/settings-section";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import { errorMessage, type AccountConfig, type OutcomeRule } from "@/lib/api";
import { minutesToWords } from "@/lib/labels";
import { useSaveOutcomeRules } from "@/lib/queries";

/**
 * The no-time branch's slug. A rule, not an outcome — see the server's lib/outcomes.ts.
 */
const CALLBACK_NO_TIME = "callback_no_time";

/**
 * Slugs the server still sends and this page deliberately does not render. See NOT_SETTINGS
 * for why each one left; they are excluded by name so that `ungrouped` stays a real safety net
 * for a *ninth* outcome rather than a bin for the four we retired on purpose.
 *
 * Module scope, not the component body: it is a constant, and one declared next to GROUPS is
 * one a reader finds when they wonder why a rule the API returned is not on screen.
 */
const RETIRED = ["dropped", "not_interested", "dnc", "wrong_number"];

const GROUPS = [
  {
    key: "asked",
    title: "They asked to be called back",
    outcomes: ["callback_requested", "callback_no_time"],
    hint: "Two rows, because they are two different situations. Somebody who named an hour is called at that hour. Somebody who just said “call me back” has to be given a time by this account — or not called at all, if a guess is not good enough for you.",
  },
  {
    key: "unreached",
    title: "Nobody spoke to the agent",
    outcomes: ["voicemail", "no_answer", "never_spoke"],
    hint: "These never name a time, so the delay is the whole policy. Whether they reach us at all is your hook's choice — these switches are the backstop if one arrives anyway.",
  },
] as const;

/**
 * The four outcomes that are no longer settings on this page, and why each one left.
 *
 * Arun, looking at the old version: *"why is not interested here? why is call dropped here? why
 * do we need a wrong number here?"*
 *
 * Two of them are dead rows. Since the hook stopped posting anything the app cannot act on,
 * nothing generated by this app ever sends `not_interested` or `dropped` — a call this app never
 * hears about is a call it cannot make, so *not sending it* is the whole behaviour, and a delay
 * and an attempt cap for it are settings for an event that does not arrive.
 *
 * The other two were never settings at all. `dnc` and `wrong_number` are posted precisely
 * because nothing will be dialled: they block the number for every future callback on the
 * account. Their rows showed a switch and three empty inputs with "—" in them, which invites
 * the reader to look for a policy that does not exist.
 *
 * The rules still exist server-side and are still honoured, because a hand-written hook may
 * post any of the eight and the app must do something sane with it: recorded, not dialled. That
 * is stated once below instead of being four rows of controls.
 */
const NOT_SETTINGS = [
  {
    title: "“Do not call” and “wrong number” block the number",
    body:
      "A call posted as either one is recorded, and the number goes on this account's Do-not-call list — which stops every future callback for it, whether it comes from another call, another agent, or a CRM feed. That is not a policy choice, so there is nothing here to switch.",
  },
  {
    title: "Anything else your hook posts is recorded and never dialled",
    body:
      "The hook this app generates only posts a callback request, a do-not-call, or a wrong number — everything else stays in 8loop. A hand-written hook can post any outcome, and one that arrives without a rule here is kept in the queue as “not a callback” with its reason, so nothing is lost and nobody is rung.",
  },
];


export function OutcomesSection({
  accountId,
  config,
}: {
  accountId: string;
  config: AccountConfig;
}) {
  const save = useSaveOutcomeRules(accountId);
  const [rules, setRules] = useState<OutcomeRule[]>(config.outcome_rules);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!dirty) setRules(config.outcome_rules);
  }, [config.outcome_rules, dirty]);

  const set = (outcome: string, patch: Partial<OutcomeRule>) => {
    setRules((rs) => rs.map((r) => (r.outcome === outcome ? { ...r, ...patch } : r)));
    setDirty(true);
  };

  const byOutcome = useMemo(
    () => new Map(rules.map((r) => [r.outcome, r] as const)),
    [rules],
  );
  // Anything the server sends that this file does not place — so adding a ninth outcome
  // server-side surfaces here instead of vanishing.
  const ungrouped = rules.filter(
    (r) =>
      !GROUPS.some((g) => (g.outcomes as readonly string[]).includes(r.outcome)) &&
      !RETIRED.includes(r.outcome),
  );

  return (
    <>
      <SettingsSection
        title="Which finished calls become a callback"
        description="Your hook posts only what you mapped on the agent's own page — a callback request, or a number to block. Everything else never leaves 8loop, so it is not on this page either. What is left here is the timing: how long after the call to dial, and whether a particular ending is worth dialling at all. A change takes effect on the next call and nothing is reinstalled; switching one off keeps the record and dials nobody."
        footer={
          <>
            <Button
              variant="ghost"
              size="sm"
              disabled={!dirty || save.isPending}
              onClick={() => {
                setRules(config.outcome_rules);
                setDirty(false);
              }}
            >
              Discard
            </Button>
            <Button
              size="sm"
              className="min-w-[120px]"
              disabled={!dirty || save.isPending}
              onClick={() =>
                save.mutate(
                  rules
                    .filter((r) => !r.never_dialled)
                    .map((r) => ({
                      outcome: r.outcome,
                      enabled: r.enabled,
                      delay_minutes: r.delay_minutes,
                      retry_delay_minutes: r.retry_delay_minutes,
                      max_attempts: r.max_attempts,
                    })),
                  {
                    onSuccess: () => {
                      setDirty(false);
                      toast.success("Outcome rules saved.");
                    },
                    onError: (e) => toast.error(errorMessage(e)),
                  },
                )
              }
            >
              {save.isPending ? "Saving…" : "Save outcomes"}
            </Button>
          </>
        }
      >
        {GROUPS.map((group) => {
          const groupRules = group.outcomes
            .map((o) => byOutcome.get(o))
            .filter((r): r is OutcomeRule => Boolean(r));
          if (groupRules.length === 0) return null;
          // Every group left on this page is editable; the two that were not are now facts
          // rather than rows (see NOT_SETTINGS). The prop survives for `ungrouped`, which is
          // the only path a never-dialled outcome can still take onto this screen.
          const editable = true;

          return (
            <div key={group.key} className="space-y-2">
              <div>
                <p className="text-sm font-medium text-foreground">{group.title}</p>
                <p className="text-xs text-muted-foreground">{group.hint}</p>
              </div>

              {/*
                The column heads sit inside each group rather than once at the top,
                because the last group has no columns at all and a header row spanning
                a table whose bottom third is a single dash reads as broken.

                `px-4` matches the row padding below. Without it the header grid starts
                four pixels left of the cells it names, which at this size is enough to
                make "Attempt cap" look like it belongs to the column on its left.
              */}
              {editable && (
                <div className="hidden grid-cols-[1fr_112px_112px_92px] gap-3 px-4 text-[11px] text-muted-foreground md:grid">
                  {/* Units in the heading, because three unlabelled number boxes in a
                      row is a guess: "Call after 30" could be minutes or hours, and
                      "Attempt cap 3" could plausibly be days. The `aria-label` on each
                      input says so already — it should not be the only place it does. */}
                  <span />
                  <span>Call after (minutes)</span>
                  <span>Retry after (minutes)</span>
                  <span>Attempt cap (dials)</span>
                </div>
              )}

              <div className="divide-y divide-border rounded-lg border border-border">
                {groupRules.map((rule) => (
                  <OutcomeRow
                    key={rule.outcome}
                    rule={rule}
                    config={config}
                    editable={editable}
                    onChange={(patch) => set(rule.outcome, patch)}
                  />
                ))}
              </div>
            </div>
          );
        })}

        {/* Facts, not controls. See NOT_SETTINGS for why these four rows left this page. */}
        <div className="space-y-2">
          {NOT_SETTINGS.map((n) => (
            <div key={n.title} className="rounded-lg border border-border px-4 py-3">
              <p className="text-sm font-medium">{n.title}</p>
              <p className="text-muted-foreground mt-0.5 text-sm">{n.body}</p>
            </div>
          ))}
        </div>

        {ungrouped.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-medium text-warning">
              Outcomes this screen does not know how to group
            </p>
            <div className="divide-y divide-border rounded-lg border border-border">
              {ungrouped.map((rule) => (
                <OutcomeRow
                  key={rule.outcome}
                  rule={rule}
                  config={config}
                  editable={!rule.never_dialled}
                  onChange={(patch) => set(rule.outcome, patch)}
                />
              ))}
            </div>
          </div>
        )}

        <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
          <Info className="mt-0.5 size-3 shrink-0" />
          <span>
            “Retry after” and “Attempt cap” left blank inherit the account's own values from
            Retries &amp; defaults ({minutesToWords(config.account.retry_delay_minutes)},{" "}
            {config.account.max_attempts_per_channel} attempts). Fill one in only where an outcome
            genuinely differs — a voicemail is worth trying again sooner than a no-answer, and
            almost nothing else is.
          </span>
        </p>
      </SettingsSection>

    </>
  );
}

function OutcomeRow({
  rule,
  config,
  editable,
  onChange,
}: {
  rule: OutcomeRule;
  config: AccountConfig;
  editable: boolean;
  onChange: (patch: Partial<OutcomeRule>) => void;
}) {
  const isProduct = rule.outcome === "callback_requested";
  const isNoTime = rule.outcome === CALLBACK_NO_TIME;
  return (
    <div className="grid grid-cols-1 items-center gap-3 px-4 py-3 md:grid-cols-[1fr_112px_112px_92px]">
      <div className="flex items-start gap-3">
        <Switch
          checked={rule.enabled && !rule.never_dialled}
          disabled={rule.never_dialled}
          aria-label={`Call back on ${rule.label}`}
          onCheckedChange={(v) => onChange({ enabled: v })}
        />
        <div className="min-w-0">
          <p className={cn("text-sm", !rule.enabled && !rule.never_dialled && "text-muted-foreground")}>
            {rule.label}
          </p>
          <p className="font-mono text-[11px] text-muted-foreground">{rule.outcome}</p>
          {/*
            The one switch on this page that should never be flipped. Said here, next to
            it, rather than removed: the server accepts it, and a control that silently
            ignores you is worse than one that warns you.
          */}
          {isProduct && !rule.enabled && (
            <p className="mt-0.5 flex items-start gap-1.5 text-xs text-warning">
              <AlertTriangle className="mt-0.5 size-3 shrink-0" />
              <span>
                Off — a person who asks to be called back gets recorded and never dialled, which is
                this whole app doing nothing. To stop calls temporarily, use Pause on the accounts
                page instead.
              </span>
            </p>
          )}
          {isProduct && rule.enabled && (
            <p className="mt-0.5 text-xs text-muted-foreground">
              Dialled at the time they asked for, moved forward if that falls outside your
              calling hours. There is no delay to set — they already named one.
            </p>
          )}
          {isNoTime && (
            <p className="mt-0.5 text-xs text-muted-foreground">
              {rule.enabled
                ? "“Call me back” with no hour named. This app has to pick a time, so it uses the delay beside this row."
                : "Off: a request with no time is recorded and never dialled. It still appears in the queue, as “not a callback”, with the reason on it — so nobody has to wonder what became of the call."}
            </p>
          )}
          {/*
            `never_dialled` reaches this row only through `ungrouped` — the four slugs that carry
            it today are retired from the page (see RETIRED). Kept, rather than deleted with them,
            because it is the safety net's whole job: a suppression the server adds later must not
            arrive here wearing three editable delay boxes.
          */}
          {rule.never_dialled && (
            <p className="mt-0.5 text-xs text-muted-foreground">
              Recorded, then the number goes on Do not call.
            </p>
          )}
        </div>
      </div>

      {editable ? (
        <>
          {/* A request that named an hour has no delay to set: that is what "as requested"
              means, and an input here would be a number the scheduler never reads. */}
          {isProduct ? (
            <NotACell>at the time they asked</NotACell>
          ) : (
            <NumberCell
              label={`Call after, minutes, for ${rule.label}`}
              value={rule.delay_minutes}
              placeholder={String(rule.suggested_delay_minutes)}
              disabled={!rule.enabled}
              hint={minutesHint(rule.delay_minutes, "suggested")}
              onChange={(v) => onChange({ delay_minutes: v })}
            />
          )}
          {/* Retries and the cap belong to the outcome, and both callback rows ARE one
              outcome — the queued row's outcome is `callback_requested` either way, so that
              is the rule the retry path reads. Two sets of numbers would be two answers to
              one question, and only one of them would be obeyed. */}
          {isNoTime ? (
            <>
              <NotACell>as the row above</NotACell>
              <NotACell>as the row above</NotACell>
            </>
          ) : (
            <>
              <NumberCell
                label={`Retry after, minutes, for ${rule.label}`}
                value={rule.retry_delay_minutes}
                placeholder={String(config.account.retry_delay_minutes)}
                disabled={!rule.enabled}
                hint={minutesHint(rule.retry_delay_minutes, "account default")}
                onChange={(v) => onChange({ retry_delay_minutes: v })}
              />
              <NumberCell
                label={`Attempt cap for ${rule.label}`}
                value={rule.max_attempts}
                placeholder={String(config.account.max_attempts_per_channel)}
                disabled={!rule.enabled}
                hint={rule.max_attempts === null ? "account default" : undefined}
                onChange={(v) => onChange({ max_attempts: v })}
              />
            </>
          )}
        </>
      ) : (
        <span className="hidden text-xs text-muted-foreground md:col-span-3 md:block">—</span>
      )}
    </div>
  );
}

/**
 * What to print under a minutes box.
 *
 * A *filled* box is where this screen was hardest to read: the seeded delays run to three
 * figures — 90, 120, 180, and 240 on any account that stored it before the never-spoke default
 * came down to 30 — and nobody looks at 240 and thinks "four hours". So any figure of an hour
 * or more says so in words underneath, which is how a stale 240 gets noticed at all. The same
 * trick the queue already uses on scheduled times.
 *
 * An empty box names its source and nothing else. The first version also spelled the
 * inherited number out, and "30 minutes (account default)" wrapped onto two lines in a
 * 112px column, which left every row a different height and repeated a number the
 * placeholder was already showing one line above it.
 */
function minutesHint(value: number | null, source: string): string | undefined {
  if (value === null) return source;
  return value >= 60 ? minutesToWords(value) : undefined;
}

/**
 * A cell where a number would be a lie.
 *
 * Not a disabled input: a greyed-out box invites somebody to try to type in it and then
 * wonder what they did wrong. This says what governs the timing instead, which is the
 * question the empty box would have raised.
 */
function NotACell({ children }: { children: React.ReactNode }) {
  return (
    <p className="self-center text-[11px] text-muted-foreground md:text-center">{children}</p>
  );
}

/**
 * A number input where empty means "inherit".
 *
 * Blank rather than a pre-filled default on purpose: a field showing 30 cannot tell
 * you whether someone chose 30 or whether it came from the account. The placeholder
 * shows what will be used, and the value shows what has been decided here.
 */
function NumberCell({
  label,
  value,
  placeholder,
  hint,
  disabled,
  onChange,
}: {
  label: string;
  value: number | null;
  placeholder: string;
  hint?: string;
  disabled?: boolean;
  onChange: (value: number | null) => void;
}) {
  return (
    <div className="space-y-1">
      <Input
        type="number"
        min={0}
        aria-label={label}
        className="h-8"
        disabled={disabled}
        placeholder={placeholder}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
      />
      {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
    </div>
  );
}
